import type { FormSubmitEvent, FormError } from "#ui/types";
import type { FormSubmit, Err } from "~/types";

export type ValidationErrors = Record<string, string[]>;

export type UseSubmitOptions<T = unknown> = {
  onSuccess?: (result: T) => unknown;
  onError?: (error: Err) => unknown;
};

export function useSubmit<T, S = object>(
  fetchable: () => Promise<T>,
  options: UseSubmitOptions<T> = {},
  form?: FormSubmit<S>
) {
  const validationErrors = ref<ValidationErrors>({});
  const error = ref<Error | null>(null);
  const inProgress = ref(false);
  const succeeded = ref<boolean | null>(null);

  async function submit(_event?: FormSubmitEvent<S>) {
    validationErrors.value = {};
    error.value = null;
    inProgress.value = true;
    succeeded.value = null;

    try {
      const data = await fetchable();
      succeeded.value = true;
      options?.onSuccess?.(data);
      return data;
    } catch (err: unknown) {
      const errorWithData = err as Err;
      error.value = err as Error;
      succeeded.value = false;
      options?.onError?.(errorWithData);
      validationErrors.value = errorWithData.data?.errors ?? {};

      const backendErrors = ref<FormError[]>([]);

      for (const [key, errorsArray] of Object.entries(validationErrors.value)) {
        if (errorsArray.length > 0) {
          backendErrors.value.push({ message: errorsArray[0], path: key });
        }
      }

      const responseStatus = (err as { response?: { status?: number } })
        .response?.status;
      if (
        responseStatus === 422 &&
        form?.value &&
        backendErrors.value.length > 0
      ) {
        scrollToFirstError(backendErrors.value);
        form.value.setErrors(backendErrors.value);
      }

      /**
       * Rethrow only what nobody has shown the visitor yet. A 422 lands on the
       * form above; the rest of what `$larafetch` surfaces is already a toast
       * or the error page. Rethrowing those turned expected outcomes — a wrong
       * OTP, the daily SMS limit on sign-in — into unhandled rejections, since
       * nothing awaits `submit()` from a button, and Sentry logged each one as
       * a crash.
       *
       * A failed submit therefore resolves to `undefined`. Callers that act on
       * success must check the result rather than rely on a throw.
       */
      if (responseStatus !== 422 && !wasShownToVisitor(err)) throw err;
    } finally {
      inProgress.value = false;
    }
  }

  return {
    submit,
    inProgress,
    succeeded,
    validationErrors,
    error,
  };
}

function scrollToFirstError(errors: FormError[]) {
  const element = document.getElementsByName(errors[0]?.path)[0];
  if (element) {
    element?.scrollIntoView({ behavior: "smooth", block: "center" });
  }
}
